import { FormControl, FormHelperText, makeStyles } from '@material-ui/core';
import { ArrowDropDown, ArrowDropUp } from '@material-ui/icons';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ExactTagsWithExclusionTagsFilter } from '../../../generated/model/exactTagsWithExclusionTagsFilter';
import { Classifier, TagsWithExclusions } from '../../../generated/model/models';
import { useDefaultValueWatcher } from '../../../hooks/useDefaultValueWatcher';
import { useOnChange } from '../../../hooks/useOnChange';
import InputAutocomplete, { AutocompleteOption } from '../../Inputs/Autocomplete';
import {
  formatClassifiersToOptions,
  mapSelectedAutocompleteOptions,
} from '../../Inputs/Autocomplete/autocomplete.utils';
import ReadableStatementView from '../../Inputs/ReadableStatementView';
import SwitchCompaniesUnknowns from '../../Inputs/SwitchCompaniesUnknowns';

interface Props {
  root?: boolean;
  items?: Classifier[];
  defaultValue?: ExactTagsWithExclusionTagsFilter;
  onChange: (value: ExactTagsWithExclusionTagsFilter) => void;
  active: boolean;
}

interface TagsDisplayState {
  excludeValues: string[];
  includeValues: string[];
}

const ACTIVE_PLUS_STATUS = 'Active+';
const ACTIVE_REGISTRY = 'Active (from registry)';
const ACTIVE_INFERRED = 'Active (inferred)';

const DEFAULT_TAGS = { includeValues: [ACTIVE_INFERRED, ACTIVE_REGISTRY], excludeValues: [] };

const StatusTagsExclusionFilterInput: React.FC<Props> = React.memo(
  ({
    root,
    active,
    items = [],
    defaultValue = {
      includeEmpty: false,
      value: {
        tags: DEFAULT_TAGS,
      },
    },
    onChange,
  }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [displayState, setDisplayState] = useState<TagsDisplayState>(
      displayDefaultValues(defaultValue?.value?.tags),
    );
    const [state, setState] = useState<ExactTagsWithExclusionTagsFilter>(defaultValue);
    // Synchronize displayState with state.value.tags
    React.useEffect(() => {
      setDisplayState(displayDefaultValues(state.value?.tags));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.value?.tags]);
    const [toggleOpenState, setTogglerOpenState] = useState<boolean>(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const options: AutocompleteOption[] = useMemo(() => {
      const optionBuilder: AutocompleteOption[] = [];
      optionBuilder.push({ label: ACTIVE_PLUS_STATUS, value: ACTIVE_PLUS_STATUS });
      optionBuilder.push({ label: ACTIVE_REGISTRY, value: ACTIVE_REGISTRY });
      optionBuilder.push({ label: ACTIVE_INFERRED, value: ACTIVE_INFERRED });
      optionBuilder.push(...formatClassifiersToOptions(items));
      return optionBuilder;
    }, [items]);

    useDefaultValueWatcher({ defaultValue, state, setState });
    useOnChange(onChange, state, root);

    function getValuesNames(value: string[]) {
      const valuesList = value.map((v) => options.find((o) => o.value === v)?.label);
      return valuesList.join(', ');
    }

    function displayDefaultValues(tags?: TagsWithExclusions) {
      const resolveStatuses = (values?: string[]) => {
        if (!values) {
          return [];
        }

        if (values.includes(ACTIVE_INFERRED) && values.includes(ACTIVE_REGISTRY)) {
          const filtered = values.filter((v) => v !== ACTIVE_INFERRED && v !== ACTIVE_REGISTRY);
          if (filtered.includes(ACTIVE_PLUS_STATUS)) {
            return [...filtered];
          } else {
            return [ACTIVE_PLUS_STATUS, ...filtered];
          }
        } else {
          return values;
        }
      };

      return tags
        ? {
            includeValues: resolveStatuses(tags.includeValues ?? []),
            excludeValues: resolveStatuses(tags.excludeValues ?? []),
          }
        : DEFAULT_TAGS;
    }

    const rs: any[] = [];

    if (state.includeEmpty) {
      rs.push({ label: 'Excluded:', value: 'Inactive companies' });
    }

    if (displayState.includeValues.length) {
      rs.push({ label: 'Included:', value: getValuesNames(displayState.includeValues) });
    }
    if (displayState.excludeValues.length) {
      rs.push({ label: 'NOT included:', value: getValuesNames(displayState.excludeValues) });
    }

    return (
      <>
        {active ? (
          <>
            <SwitchCompaniesUnknowns
              onChange={(value) => {
                setState({ ...state, includeEmpty: value });
              }}
              unknownSwitchLabel={'companies:filterInputs.excludeCompaniesIfInactive'}
              checked={state.includeEmpty}
            />

            <FormControl className={`form-control ${classes.root}`}>
              <InputAutocomplete
                defaultValue={mapSelectedAutocompleteOptions(options, displayState.includeValues)}
                onChange={(includeValue) => {
                  setDisplayState({
                    includeValues: includeValue.map((option: AutocompleteOption) => option.value),
                    excludeValues: displayState.excludeValues,
                  });
                  setState({
                    ...state,
                    value: {
                      tags: {
                        includeValues: includeValue.flatMap((option: AutocompleteOption) =>
                          option.value === ACTIVE_PLUS_STATUS
                            ? [ACTIVE_INFERRED, ACTIVE_REGISTRY]
                            : [option.value],
                        ),
                        excludeValues:
                          state.value && state.value.tags ? state.value.tags.excludeValues : [],
                      },
                    },
                  });
                }}
                options={options}
              />
              <div
                className={classes.tagToggler}
                onClick={() => setTogglerOpenState(!toggleOpenState)}
              >
                <FormHelperText>{t('app:notIncluded')}</FormHelperText>
                {toggleOpenState ? <ArrowDropUp /> : <ArrowDropDown />}
              </div>

              {toggleOpenState ? (
                <InputAutocomplete
                  defaultValue={mapSelectedAutocompleteOptions(options, displayState.excludeValues)}
                  onChange={(excludeValue) => {
                    setDisplayState({
                      includeValues: displayState.includeValues,
                      excludeValues: excludeValue.map((option: AutocompleteOption) => option.value),
                    });
                    setState({
                      ...state,
                      value: {
                        tags: {
                          excludeValues: excludeValue.flatMap((option: AutocompleteOption) =>
                            option.value === ACTIVE_PLUS_STATUS
                              ? [ACTIVE_INFERRED, ACTIVE_REGISTRY]
                              : [option.value],
                          ),
                          includeValues:
                            state.value && state.value.tags ? state.value.tags.includeValues : [],
                        },
                      },
                    });
                  }}
                  options={options}
                />
              ) : null}
            </FormControl>
          </>
        ) : (
          <ReadableStatementView rows={rs} />
        )}
      </>
    );
  },
);

const useStyles = makeStyles((theme) => ({
  root: {
    '.Mui-expanded': {
      margin: 0,
    },
  },
  tagToggler: {
    display: 'flex',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: 'center',
    justifyContent: 'flex-start',
    fontSize: theme.typography.fontSize - 2,
  },
}));

export default StatusTagsExclusionFilterInput;
